import React, { useState } from 'react';
import Form, { useForm } from '../../molecules/Form';
import Button from '../../molecules/Button';
import Toast from '../../molecules/Toast';
import DragableComponent from '../DraggableComponent/DragableComponent';
import CenterModal from '../../molecules/Modal/CenterModal';
import Loaders from '../Loaders';
import { useLocation, useNavigate } from 'react-router-dom';
import { MappingHolder } from '../CreateCompaign/CreateCompaign.styles';
import {
  filterRowsByMapping,
  mapRowsToPayload,
  normalize,
  calculateStatisticsFromMappedColumns,
  removeDuplicatesByMappedColumns,
} from '../../../helpers/common';

const DataMaping = ({ setData, data, setAddLeadsDataTab, setCsvInfo, domains_data }) => {
  const [form] = useForm();
  const [dragItems, setDragItems] = useState({ root: [], container1: [] });
  const [loading, setLoading] = useState(false);

  const predefinedFields = [
    { key: 'first_name', label: 'First Name', editable: false, required: true },
    { key: 'last_name', label: 'Last Name', editable: false, required: true },
    { key: 'email', label: 'Email', editable: false, required: false },
    { key: 'company_name', label: 'Company Name', editable: false, required: false },
    { key: 'linkedin_url', label: 'LinkedIn URL', editable: false, required: false },
    { key: 'linkedin_id', label: 'LinkedIn ID', editable: false, required: false },
    { key: 'website', label: 'Website', editable: false, required: false },
    { key: 'person_city', label: 'Person City', editable: false, required: false },
    { key: 'person_country', label: 'Person Country', editable: false, required: false },
    { key: 'title', label: 'Title', editable: false, required: false },
    { key: 'industry', label: 'Industry', editable: false, required: false },
    { key: 'phone_number', label: 'Phone Number', editable: false, required: false },
    { key: 'profile_photo', label: 'Profile Picture', editable: false, required: false },
    { key: 'job_description', label: 'Job Description', editable: false, required: false },
    { key: 'company_description', label: 'Company Description', editable: false, required: false },
  ];

  const [dragableData, setDragableData] = useState(predefinedFields);

  const handleAddField = () => {
    setDragableData(prev => {
      const updated = [...prev, { key: 'newKey', label: 'New Field', editable: true, required: false }];

      setDragItems(prevItems => ({
        ...prevItems,
        root: [...prevItems.root, null],
      }));

      return updated;
    });
  };

  const handleEditLabel = (index, newLabel) => {
    setDragableData(prev => {
      const updated = [...prev];
      updated[index].label = newLabel;
      updated[index].key = newLabel.toLowerCase().replace(/\s+/g, '_');
      return updated;
    });
  };
  const handelSubmit = async () => {
    const getMapped = key => {
      const index = dragableData.findIndex(item => item.key === key);
      return dragItems.root?.[index]?.rowKey || null;
    };

    const first = getMapped('first_name');
    const last = getMapped('last_name');
    const email = getMapped('email');
    const company = getMapped('company_name');
    const profileId = getMapped('profile_id');

    const hasValidMapping =
      (first && last && email) ||
      (first && last && company && profileId) ||
      (first && last && company && !email) ||
      (profileId && !first && !last && !email && !company);

    if (!hasValidMapping) {
      Toast({
        type: 'error',
        message: `Please map the rows correctly:
                    1) first_name + last_name + email
                    2) first_name + last_name + company_name
                    4) profile_id`,
      });
      return;
    }

    const rows = data?.step1?.rows || [];
    const filtered = filterRowsByMapping(rows, {
      first,
      last,
      email,
      company,
      profileId,
    });

    // Remove duplicates based on mapped columns
    const { rows: deduplicatedRows, duplicateCount } = removeDuplicatesByMappedColumns(
      filtered,
      dragableData,
      dragItems,
    );

    const mappedRows = mapRowsToPayload(deduplicatedRows, dragableData, dragItems, predefinedFields);
    const statistics = calculateStatisticsFromMappedColumns(
      rows,
      dragableData,
      dragItems,
      domains_data,
      data?.step1?.columnNames,
    );
    if (setCsvInfo) {
      setCsvInfo({
        totalContacts: rows.length,
        totalEmails: statistics.totalEmails,
        hasMappedEmail: Boolean(email),
        uniqueRowsCount: mappedRows.length,
        duplicateEmails: statistics.duplicateEmails,
        blockedEmails: statistics.blockedRows,
        duplicateCountByMapping: duplicateCount,
      });
    }

    const step2 = {
      name: data?.step1?.list_name,
      leadList: mappedRows,
      leads_type: 'csv_leads',
      tagId: data?.step1?.tag,
      // includeDuplicates: false,
      // columnNames: columnMapping,
    };
    setData(prev => ({ ...prev, step2: step2 }));
    setAddLeadsDataTab('leadsData');
  };

  return (
    <>
      <CenterModal open={loading} setOpen={setLoading} width="600" noHead>
        <div className="leads-modal">
          <strong className="heading">Processing your leads...</strong>
          <p>CSV file has many leads, so this may take some time ⏳</p>
          <Loaders dotsLoader dotsBottom />
        </div>
      </CenterModal>

      <MappingHolder>
        <div className="col-maping">
          <div className="head">
            <strong className="heading">Map your CSV columns</strong>
            <p>
              Drag and drop CSV columns to map them with the required fields. Fields marked as required must be mapped.
            </p>
          </div>
          <Form form={form} onSubmit={handelSubmit}>
            <div className="col-wrap">
              <DragableComponent
                initialItems={{
                  root: Array(dragableData.length).fill(null),
                  // Each CSV column gets a unique ID and a unique rowKey
                  // (matching handleOnDrop) so duplicates stay independent.
                  container1:
                    data?.step1?.columnNames
                      ?.filter(name => name && name.trim() !== '')
                      ?.map((name, index) => ({
                        id: `csv-col-${index}`,
                        title: name,
                        rowKey: `${normalize(name)}_${index}`,
                        originalIndex: index,
                      })) || [],
                }}
                onItemsChange={setDragItems}
                dragableData={dragableData}
                onEditLabel={handleEditLabel}
                onAddField={handleAddField}
              />
            </div>
            <div className="btn-holder">
              <div className="btn-wrap">
                <Button
                  newStyle
                  rounded
                  variant="outline"
                  width="155px"
                  type="button"
                  onClick={() => setAddLeadsDataTab('leads')}>
                  Back
                </Button>
              </div>
              <Button newStyle rounded variant="darkBlue" type="submit" width="155px">
                Next
              </Button>
            </div>
          </Form>
        </div>
      </MappingHolder>
    </>
  );
};

export default DataMaping;
